import type { LocalVault } from '@signbento/storage'
import { isTemplatePurgeDue } from './recently-deleted.js'

export interface PurgeExpiredTemplatesOptions {
  readonly vault: LocalVault
  readonly now: Date
}

/** Removes expired soft-deleted templates without loading the PDF/editor pipeline at app boot. */
export async function purgeExpiredTemplates(
  options: PurgeExpiredTemplatesOptions,
): Promise<readonly string[]> {
  const { vault, now } = options
  const purged: string[] = []
  for (const template of await vault.listTemplates()) {
    const deletedAt = template.deleted_at
    if (deletedAt === undefined || !isTemplatePurgeDue(deletedAt, now)) continue
    await removeTemplateRecord(vault, template.id)
    purged.push(template.id)
  }
  return purged
}

async function removeTemplateRecord(vault: LocalVault, templateId: string): Promise<void> {
  const revision = await vault.getRecordRevision('template', templateId)
  await vault.deleteTemplate(templateId, revision === null ? {} : { expected_revision: revision })
  for (const key of await vault.listBlobKeys()) {
    if (key.startsWith(`${templateId}/`)) await vault.deleteBlob(key)
  }
}

/**
 * Delete Permanently from Recently Deleted (PRD §5.1): the record and its own blobs go now instead
 * of at the 30-day boundary. Only a template already in Recently Deleted can go, so an active one is
 * never lost to a stray call. Drafts and agreements made from it hold their own copies and stay.
 */
export async function deleteTemplatePermanently(options: {
  readonly vault: LocalVault
  readonly template_id: string
}): Promise<boolean> {
  const template = await options.vault.getTemplate(options.template_id)
  if (template === null || template.deleted_at === undefined) return false
  await removeTemplateRecord(options.vault, template.id)
  return true
}
